import { supabase } from '../../lib/supabase'
import type { SwapState } from '../agent/agentSwapStore'
import { todayIso } from '../reports/reportDates'
import { getTeamAvailability } from './availabilityBoardStore'
import { workConflictFor, workWindowOf } from './availabilityOverlay'
import { missingQualificationWarning, overTourWarnings } from './eligibility'
import { getEligibilityFacts } from './eligibilityStore'
import { formatDateRange } from './leaveRequestQueue'

// Every shift swap a signed-in supervisor (or administrator) can see that both
// agents have agreed to, or that has been decided, with its latest decision and
// the checks a supervisor reads before approving. A proposal still waiting on
// the colleague, or one they declined, is the agents' business and not listed.
export type TeamShiftSwap = {
  id: string
  teamId: string
  requesterId: string
  requesterName: string
  counterpartName: string
  // The first of the two dates, what the inbox sorts on.
  sortDate: string
  // "Ofc. A gives Oct 7 (0700–1500); Ofc. B gives Oct 9 (0900–1700)": what the inbox prints.
  detail: string
  message?: string
  state: SwapState
  // What the supervisor should weigh before approving: an agent marked unavailable
  // for the hours they would take, over their tour of duty for a pay period, or
  // without the qualification the assignment they would take asks for. Advisory only.
  advisories: string[]
  decisionComment?: string
  decidedByName?: string
}

type SwapRow = {
  id: string
  team_id: string
  requester_employee_id: string
  requester_date: string
  counterpart_employee_id: string
  counterpart_date: string
  requester_name: string | null
  counterpart_name: string | null
  requester_shift: string | null
  counterpart_shift: string | null
  message: string | null
  state: SwapState
  decisions: { comment: string | null; decided_at: string; decided_by_name: string }[] | null
}

const COLUMNS =
  'id, team_id, requester_employee_id, requester_date, counterpart_employee_id, counterpart_date, requester_name, counterpart_name, requester_shift, counterpart_shift, message, state, decisions(comment, decided_at, decided_by_name)'

export async function getTeamShiftSwaps(): Promise<TeamShiftSwap[]> {
  const { data, error } = await supabase.from('shift_swaps').select(COLUMNS).in('state', ['accepted', 'approved', 'denied'])
  if (error || !data) return []
  const rows = data as unknown as SwapRow[]
  if (rows.length === 0) return []

  const accepted = rows.filter((row) => row.state === 'accepted')
  const [availability, facts] = await Promise.all([
    getTeamAvailability(todayIso()),
    getEligibilityFacts(
      accepted.flatMap((row) => [row.requester_employee_id, row.counterpart_employee_id]),
      accepted.flatMap((row) => [row.requester_date, row.counterpart_date]),
    ),
  ])

  return rows.map((row) => {
    const requester = row.requester_name ?? 'An agent'
    const counterpart = row.counterpart_name ?? 'A colleague'
    const advisories: string[] = []
    if (row.state === 'accepted') {
      // The requester takes the colleague's shift, and the colleague the requester's.
      if (workConflictFor(row.requester_employee_id, row.counterpart_date, workWindowOf(row.counterpart_shift ?? ''), availability.rules, availability.exceptions)) {
        advisories.push(`${requester} is marked unavailable for the hours they would take.`)
      }
      if (workConflictFor(row.counterpart_employee_id, row.requester_date, workWindowOf(row.requester_shift ?? ''), availability.rules, availability.exceptions)) {
        advisories.push(`${counterpart} is marked unavailable for the hours they would take.`)
      }

      // Each agent gives up the shift they hold and takes the other's, both
      // read from the live schedule, which is what an approval exchanges.
      const hoursOn = (employeeId: string, date: string) => facts.entriesOn(employeeId, date).reduce((sum, entry) => sum + entry.hours, 0)
      const assignmentOn = (employeeId: string, date: string) => facts.entriesOn(employeeId, date)[0]?.assignmentId

      advisories.push(
        ...overTourWarnings({
          scheduled: facts.scheduled(row.requester_employee_id),
          changes: [
            { date: row.requester_date, delta: -hoursOn(row.requester_employee_id, row.requester_date) },
            { date: row.counterpart_date, delta: hoursOn(row.counterpart_employee_id, row.counterpart_date) },
          ],
          tourHours: facts.tourHours(row.requester_employee_id),
          subject: requester,
        }),
        ...overTourWarnings({
          scheduled: facts.scheduled(row.counterpart_employee_id),
          changes: [
            { date: row.counterpart_date, delta: -hoursOn(row.counterpart_employee_id, row.counterpart_date) },
            { date: row.requester_date, delta: hoursOn(row.requester_employee_id, row.requester_date) },
          ],
          tourHours: facts.tourHours(row.counterpart_employee_id),
          subject: counterpart,
        }),
      )
      for (const missing of [
        missingQualificationWarning({
          required: facts.requiredFor(assignmentOn(row.counterpart_employee_id, row.counterpart_date)),
          held: facts.held(row.requester_employee_id),
          date: row.counterpart_date,
          subject: requester,
        }),
        missingQualificationWarning({
          required: facts.requiredFor(assignmentOn(row.requester_employee_id, row.requester_date)),
          held: facts.held(row.counterpart_employee_id),
          date: row.requester_date,
          subject: counterpart,
        }),
      ]) {
        if (missing) advisories.push(missing)
      }
    }

    const decision = (row.decisions ?? []).reduce<{ comment: string | null; decided_at: string; decided_by_name: string } | undefined>(
      (latest, current) => (!latest || current.decided_at > latest.decided_at ? current : latest),
      undefined,
    )
    const sameDay = row.requester_date === row.counterpart_date
    return {
      id: row.id,
      teamId: row.team_id,
      requesterId: row.requester_employee_id,
      requesterName: requester,
      counterpartName: counterpart,
      sortDate: row.requester_date < row.counterpart_date ? row.requester_date : row.counterpart_date,
      detail: sameDay
        ? `${formatDateRange(row.requester_date, row.requester_date)}: ${requester} (${row.requester_shift ?? ''}) and ${counterpart} (${row.counterpart_shift ?? ''}) exchange shifts`
        : `${requester} gives ${formatDateRange(row.requester_date, row.requester_date)} (${row.requester_shift ?? ''}); ${counterpart} gives ${formatDateRange(row.counterpart_date, row.counterpart_date)} (${row.counterpart_shift ?? ''})`,
      message: row.message ?? undefined,
      state: row.state,
      advisories,
      decisionComment: decision?.comment ?? undefined,
      decidedByName: decision?.decided_by_name,
    }
  })
}
